import { GetServerSidePropsContext } from 'next'
import { parseCookies, setCookie } from 'nookies'

const ActiveExperimentsKEY = 'CONFIG'

export type ActiveExperiments = Record<ExperimentKey, ExperimentVariant>

export enum ExperimentKey {
    test = 'TEST',
    showKnowledgeAreaBreadcrumbs = 'SHOW_KNOWLEDGE_AREA_BREADCRUMBS',
    showUpdatedOnwithinKnowHeader = 'SHOW_UPDATED_ON_WITHIN_KNOW_HEADER',
    showMobileConversionSection = 'SHOW_MOBILE_CONVERSION_SECTION',
    showOneTimeParentPricingLink = 'SHOW_ONE_TIME_PARENT_PRICING_LINK',
    showDesktopSmartToolsSidebar = 'SHOW_DESKTOP_SMART_TOOLS_SIDEBAR',
    showProPlanPagePaddleLink = 'SHOW_PRO_PLAN_PAGE_PADDLE_LINK',
    showHomepageQRDownloadBanner = 'SHOW_HOMEPAGE_QR_DOWNLOAD_BANNER',
    showEmotionParentPage = 'SHOW_EMOTION_PARENT_PAGE',
}

export enum ExperimentVariant {
    // Testing and Development
    testBaseline = 'TEST_0',
    testVariant = 'TEST_1',

    // Navigation and UI Components
    showKnowledgeAreaBreadcrumbsBaseline = 'SHOW_KNOWLEDGE_AREA_BREADCRUMBS_VARIANT_0',
    showKnowledgeAreaBreadcrumbsVariant = 'SHOW_KNOWLEDGE_AREA_BREADCRUMBS_VARIANT_1',

    // Updated on date within know header
    showUpdatedOnwithinKnowHeaderBaseline = 'SHOW_UPDATED_ON_WITHIN_KNOW_HEADER_VARIANT_0',
    showUpdatedOnwithinKnowHeaderVariant = 'SHOW_UPDATED_ON_WITHIN_KNOW_HEADER_VARIANT_1',

    // Mobile Pages Carousel
    showMobileConversionSectionBaseline = 'SHOW_MOBILE_CONVERSION_SECTION_VARIANT_0',
    showMobileConversionSectionVariant1 = 'SHOW_MOBILE_CONVERSION_SECTION_VARIANT_1',
    showMobileConversionSectionVariant2 = 'SHOW_MOBILE_CONVERSION_SECTION_VARIANT_2',

    // One Time Parent Pricing Link
    showOneTimeParentPricingLinkBaseline = 'SHOW_ONE_TIME_PARENT_PRICING_LINK_VARIANT_0',
    showOneTimeParentPricingLinkVariant = 'SHOW_ONE_TIME_PARENT_PRICING_LINK_VARIANT_1',

    // Desktop Smart Tools Sidebar
    showDesktopSmartToolsSidebarBaseline = 'SHOW_DESKTOP_SMART_TOOLS_SIDEBAR_VARIANT_0',
    showDesktopSmartToolsSidebarVariant = 'SHOW_DESKTOP_SMART_TOOLS_SIDEBAR_VARIANT_1',

    // Pro Plan Page Paddle Link
    showProPlanPagePaddleLinkBaseline = 'SHOW_PRO_PLAN_PAGE_PADDLE_LINK_VARIANT_0',
    showProPlanPagePaddleLinkVariant = 'SHOW_PRO_PLAN_PAGE_PADDLE_LINK_VARIANT_1',

    // Homepage QR Download Banner
    showHomepageQRDownloadBannerBaseline = 'SHOW_HOMEPAGE_QR_DOWNLOAD_BANNER_VARIANT_0',
    showHomepageQRDownloadBannerVariant = 'SHOW_HOMEPAGE_QR_DOWNLOAD_BANNER_VARIANT_1',

    // Emotion Variant
    showEmotionParentPageBaseline = 'SHOW_EMOTION_PARENT_PAGE_VARIANT_0',
    showEmotionParentPageVariant = 'SHOW_EMOTION_PARENT_PAGE_VARIANT_1',
}

function getRandomVariant(variants: ExperimentVariant[]): ExperimentVariant {
    return variants[Math.floor(Math.random() * variants.length)]
}

function getStoredVariant(storedVariants: string[], variants: ExperimentVariant[]): ExperimentVariant | null {
    for (const variant of variants) {
        if (storedVariants.includes(variant)) {
            return variant
        }
    }

    return null
}

function getExperimentVariant(possibleVariants: ExperimentVariant[], storedVariants: string[]): ExperimentVariant {
    const storedVariant = getStoredVariant(storedVariants, possibleVariants)
    if (storedVariant !== null) {
        return storedVariant
    } else {
        return getRandomVariant(possibleVariants)
    }
}

export function getActiveExperiments(ctx?: GetServerSidePropsContext): ActiveExperiments {
    const storedVariants = getActiveExperimentsStore(ctx)

    const activeExperiments: ActiveExperiments = {
        TEST: getExperimentVariant([ExperimentVariant.testBaseline, ExperimentVariant.testVariant], storedVariants),
        SHOW_KNOWLEDGE_AREA_BREADCRUMBS: getExperimentVariant(
            [ExperimentVariant.showKnowledgeAreaBreadcrumbsBaseline, ExperimentVariant.showKnowledgeAreaBreadcrumbsVariant],
            storedVariants
        ),
        SHOW_UPDATED_ON_WITHIN_KNOW_HEADER: getExperimentVariant(
            [
                ExperimentVariant.showUpdatedOnwithinKnowHeaderBaseline,
                ExperimentVariant.showUpdatedOnwithinKnowHeaderVariant,
            ],
            storedVariants
        ),
        SHOW_MOBILE_CONVERSION_SECTION: getExperimentVariant(
            [
                ExperimentVariant.showMobileConversionSectionBaseline,
                ExperimentVariant.showMobileConversionSectionVariant1,
                ExperimentVariant.showMobileConversionSectionVariant2,
            ],
            storedVariants
        ),
        SHOW_ONE_TIME_PARENT_PRICING_LINK: getExperimentVariant(
            [ExperimentVariant.showOneTimeParentPricingLinkBaseline, ExperimentVariant.showOneTimeParentPricingLinkVariant],
            storedVariants
        ),
        SHOW_DESKTOP_SMART_TOOLS_SIDEBAR: getExperimentVariant(
            [ExperimentVariant.showDesktopSmartToolsSidebarBaseline, ExperimentVariant.showDesktopSmartToolsSidebarVariant],
            storedVariants
        ),
        SHOW_PRO_PLAN_PAGE_PADDLE_LINK: getExperimentVariant(
            [ExperimentVariant.showProPlanPagePaddleLinkBaseline, ExperimentVariant.showProPlanPagePaddleLinkVariant],
            storedVariants
        ),
        SHOW_HOMEPAGE_QR_DOWNLOAD_BANNER: getExperimentVariant(
            [ExperimentVariant.showHomepageQRDownloadBannerBaseline, ExperimentVariant.showHomepageQRDownloadBannerVariant],
            storedVariants
        ),
        SHOW_EMOTION_PARENT_PAGE: getExperimentVariant(
            [ExperimentVariant.showEmotionParentPageBaseline, ExperimentVariant.showEmotionParentPageVariant],
            storedVariants
        ),
    }

    storeActiveExperiments(Object.values(activeExperiments), ctx)

    return activeExperiments
}

export function getActiveExperimentsVariants() {
    const activeExperiments = getActiveExperiments()
    return Object.values(activeExperiments)
}

// Active Experiments
export function getActiveExperimentsStore(ctx?: GetServerSidePropsContext): string[] {
    const cookies = parseCookies(ctx)
    const item = cookies[ActiveExperimentsKEY]
    if (!item) {
        return []
    }
    return item.split(',')
}

export const storeActiveExperiments = (experiments: string[], ctx?: GetServerSidePropsContext) => {
    const stringifiedValue = experiments.join(',')
    setCookie(ctx, ActiveExperimentsKEY, stringifiedValue, {
        maxAge: 30 * 24 * 60 * 60,
        path: '/',
    })
}
